import { Badge, Box, Group, Text } from '@mantine/core'
import { TAlertList, useGetAlertList } from 'api/alerts/getAlertsList'
import { QueryPaginated, TDatatable } from 'components'
import Datatable, { TableLink } from 'components/datatable'
import { DatatableProps } from 'components/datatable/Datatable'
import { AlertLevelBadge } from 'components/devices/AlertLevelBadge'
import { r } from 'config/routes'
import { TFunction } from 'i18next'
import { useTranslation } from 'react-i18next'
import { MdOutlineNotificationImportant } from 'react-icons/md'
import { capitalizeFirstLetter } from 'shared/utils'
import { ExceedHeader } from 'views/panel/Alerts/Single/components/ExceedCard'
import { getDeviceRoute } from 'views/panel/DeviceGroups/Single/getDeviceRoute'
import { AlertStatusBadge } from './AlertStatusBadge'

const headers: (
  t: TFunction,
) => TDatatable<TAlertList['items'][number]>[] = t => [
  {
    id: 'date',
    label: capitalizeFirstLetter(t('generic.date')),
    colSize: 1,
    isSortable: true,
    sortKey: 'CreatedAt',
    selector: (item, { dayjs }) => (
      <Text>{dayjs(item?.createdAt).format('DD MMM, HH:mm')}</Text>
    ),
  },
  {
    id: 'device',
    label: capitalizeFirstLetter(t('sensor.device')),
    colSize: 2,
    selector: item => (
      <TableLink
        lineClamp={2}
        href={getDeviceRoute(item?.device?.type)(
          item?.deviceId,
          'measurements',
        )}
      >
        {item?.device?.name}
      </TableLink>
    ),
  },
  {
    id: 'alertRuleType',
    label: t('alert.ruleType'),
    colSize: 2,
    selector: item => (
      <Badge
        color={item.alertRuleType === 'DeviceStatus' ? 'violet' : 'blue'}
        variant="light"
      >
        {item.alertRuleType === 'DeviceStatus'
          ? t('alert.ruleTypeDeviceStatus')
          : t('alert.ruleTypeStandard')}
      </Badge>
    ),
  },
  {
    id: 'alertName',
    label: capitalizeFirstLetter(t('generic.name')),
    colSize: 2,
    isSortable: true,
    sortKey: 'Name',
    selector: item => <Text>{item.name}</Text>,
  },
  {
    id: 'exceeds',
    label: t('generic.description'),
    colSize: 2,
    selector: item => (
      <Group>
        {item?.exceeds?.slice(0, 2).map(exceed => (
          <ExceedHeader key={exceed.id} disableIcon size={'sm'} {...exceed} />
        ))}
      </Group>
    ),
  },
  {
    id: 'level',
    label: t('alert.level'),
    colSize: 1,
    isSortable: true,
    sortKey: 'Level',
    selector: item => <AlertLevelBadge level={item?.level} />,
  },
  {
    id: 'status',
    label: t('generic.status'),
    colSize: 1,
    isSortable: true,
    sortKey: 'Status',
    selector: item => (
      <AlertStatusBadge alertId={item?.id} status={item?.status} />
    ),
  },
]

export const AlertsListTable = ({
  params,
  exclude,
  withPagination = true,
}: {
  params?: Record<string, string>
  exclude?: string[]
  withPagination?: boolean
}) => {
  const { t } = useTranslation()
  const query = useGetAlertList(params)

  return (
    <QueryPaginated.Wrapper withPagination={withPagination} query={query}>
      <QueryPaginated.Data>
        {(data: TAlertList) => (
          <AlertsTable noShadow data={data.items} exclude={exclude} />
        )}
      </QueryPaginated.Data>
      <QueryPaginated.NoData
        icon={MdOutlineNotificationImportant}
        title={t('alert.noData')}
        titleNoFiltered={t('alert.noData')}
      />
    </QueryPaginated.Wrapper>
  )
}

export const AlertsTable = (
  props: Omit<DatatableProps<TAlertList['items'][number]>, 'headers' | 'href'>,
) => {
  const { t } = useTranslation()

  return (
    <Datatable
      headers={headers(t)}
      href={({ id }) => r['alert.view'](id)}
      {...props}
    />
  )
}
